/**
 * Pure page-slicing logic for the consent-form PDF.
 *
 * Extracted from ``consentPdfExport.js`` so it can be unit-tested without
 * standing up html2canvas / jsPDF / a real DOM. The exporter imports
 * ``computePageSlices`` from here and only owns the canvas-pixel
 * orchestration (creating per-page slice canvases, calling
 * ``pdf.addImage``, etc.).
 */

/**
 * Given a canvas of height ``imgHeightPx``, an A4 page height (also in
 * canvas pixels), and a sorted list of safe page-break boundaries, return
 * the list of ``[start, end]`` Y ranges to render as PDF pages.
 *
 * Rules
 *   • Snap each page break to the largest boundary that is strictly greater
 *     than the cursor AND less than or equal to ``cursor + pageHeightPx``.
 *   • If no boundary fits (a single block taller than a page) fall back to
 *     slicing at the page-height limit so we always make forward progress.
 *   • Widow-prevention: when the *only* remaining content past the chosen
 *     break is shorter than ``widowSlackPx`` AND absorbing that tail pushes
 *     the current page past its limit by ≤ ``maxOverflowPx``, swallow the
 *     tail into the current page rather than orphaning it onto its own.
 *
 * @param {number}   imgHeightPx
 * @param {number}   pageHeightPx
 * @param {number[]} [boundaries]            sorted unique Y positions; endpoints
 *                                           (0 and imgHeightPx) are normalised in.
 * @param {Object}   [opts]
 * @param {number}   [opts.widowSlackPx]     default 0 (widow absorption disabled)
 * @param {number}   [opts.maxOverflowPx]    default 0
 * @returns {Array<[number, number]>}
 */
export function computePageSlices(
    imgHeightPx,
    pageHeightPx,
    boundaries,
    { widowSlackPx = 0, maxOverflowPx = 0 } = {},
) {
    if (!Number.isFinite(imgHeightPx) || imgHeightPx <= 0) return [];
    if (!Number.isFinite(pageHeightPx) || pageHeightPx <= 0) return [];

    const safe = Array.from(new Set([0, ...(boundaries || []), imgHeightPx]))
        .filter((y) => y >= 0 && y <= imgHeightPx)
        .sort((a, b) => a - b);

    const slices = [];
    let cursor = 0;
    // Hard loop-guard — any real form produces ≤ a few dozen pages.
    let safetyTicks = 0;

    while (cursor < imgHeightPx) {
        safetyTicks += 1;
        if (safetyTicks > 10000) break;

        const maxEnd = Math.min(cursor + pageHeightPx, imgHeightPx);

        let nextBreak = -1;
        for (let i = safe.length - 1; i >= 0; i -= 1) {
            const b = safe[i];
            if (b > cursor && b <= maxEnd) {
                nextBreak = b;
                break;
            }
        }
        if (nextBreak < 0) nextBreak = maxEnd;

        const remainingAfterBreak = imgHeightPx - nextBreak;
        const overflowIfAbsorbed = (imgHeightPx - cursor) - pageHeightPx;
        if (
            widowSlackPx > 0
            && remainingAfterBreak > 0
            && remainingAfterBreak < widowSlackPx
            && overflowIfAbsorbed <= maxOverflowPx
        ) {
            nextBreak = imgHeightPx;
        }

        if (nextBreak <= cursor) break;
        slices.push([cursor, nextBreak]);
        cursor = nextBreak;
    }

    return slices;
}
